import * as debug from 'debug';
import { Coordinate } from 'ol/coordinate';
import { addLayer, viewEventsFactory, removeLayer, FetchData } from 'sdi/map';

import { assign, dispatchK, observe, assignK } from 'sdi/shape';
import {
    fetchIssuesData,
    getIssuesSynthInfo,
    QAMAPNAME,
    OSM_POINT,
    OSM_LINE,
    OSM_POLY,
    getOsmSynthInfo,
    getView,
    getIssueGeometrySynthInfo,
    LegendSelector,
} from '../queries/map';
import {
    findIssue,
    getOsmResourceId,
    getLastSelectedIssue,
    OsmResourceId,
    fetchIssueGeometryData,
} from '../queries/qa';
import { getSelectedIssueIds } from '../queries/table';
import { deselectIssue } from './qa';
import { navigateToSelection } from './route';
import { getOsmRemoteGeometries } from '../queries/osm';
import { DirectGeometryObject, Feature, Properties } from 'sdi/source';
import { uniqId } from 'sdi/util';
import { some } from 'fp-ts/lib/Option';
import { right } from 'fp-ts/lib/Either';
import {
    issueStyleIcon,
    issueStylePriority,
    issueStyleTypology,
} from '../queries/map/style';
//import { scrollToIssue } from './table';
import { __forceRefreshState } from 'sdi/app';
import { wgs84toLB72 } from '../queries/geom';
import { getTheme } from '../queries/app';

const logger = debug('sdi:events/map');

export const updateView = viewEventsFactory(
    dispatchK('port/map/view')
).updateMapView;
export const setScaleLine = () => {};

const setSymbology = assignK('map/symbology');

export const setLegendSymbology = (s: LegendSelector) => {
    assign('map/legend/symbology', s);
    if (s !== 'objects') {
        setSymbology(s);
    }
};

export const addIssueLayer = () => {
    removeLayer(QAMAPNAME, 'issues-object');
    removeLayer(QAMAPNAME, 'issues-priority');
    removeLayer(QAMAPNAME, 'issues-typology');
    addLayer(
        QAMAPNAME,
        getIssuesSynthInfo('issues-object', issueStyleIcon()),
        fetchIssuesData
    );
    addLayer(
        QAMAPNAME,
        getIssuesSynthInfo('issues-priority', issueStylePriority()),
        fetchIssuesData
    );
    addLayer(
        QAMAPNAME,
        getIssuesSynthInfo('issues-typology', issueStyleTypology()),
        fetchIssuesData
    );
    __forceRefreshState();
};
observe('qa/issues/filter/criticality', addIssueLayer);
observe('qa/issues/linked/selected', addIssueLayer);
observe('qa/data/issues', addIssueLayer);

export const updateIssueGeometryLayer = () => {
    removeLayer(QAMAPNAME, OSM_POINT);
    removeLayer(QAMAPNAME, OSM_LINE);
    removeLayer(QAMAPNAME, OSM_POLY);
    addLayer(
        QAMAPNAME,
        getIssueGeometrySynthInfo(OSM_POINT, 'Point'),
        fetchIssueGeometryData('Point')
    );
    addLayer(
        QAMAPNAME,
        getIssueGeometrySynthInfo(OSM_LINE, 'LineString'),
        fetchIssueGeometryData('LineString')
    );
    addLayer(
        QAMAPNAME,
        getIssueGeometrySynthInfo(OSM_POLY, 'Polygon'),
        fetchIssueGeometryData('Polygon')
    );
};
// observe('qa/issue/geometries', updateIssueGeometryLayer);
observe('qa/issue/geometries/collection', updateIssueGeometryLayer);
observe('qa/issues/select', updateIssueGeometryLayer);

export const selectIssueFromFeature = (featureId: number | string) =>
    findIssue(featureId).foldL(
        () => {},
        issue => {
            if (getSelectedIssueIds().find(i => issue.id === i)) {
                deselectIssue(issue);
            } else {
                //scrollToIssue(issue.id);
                getTheme().map(theme => navigateToSelection(theme, issue.id));
            }
        }
    );

export const centerOn = (center: Coordinate) =>
    updateView({
        dirty: 'geo',
        center,
    });
export const centerAndZoomOn = (center: Coordinate) =>
    updateView({
        dirty: 'geo',
        center,
        zoom: Math.max(getView().zoom, 15),
    });

const { markOsm, unMarkOsm, getOsmMark } = (() => {
    let mark: OsmResourceId | null = null;
    const markOsm = (id: OsmResourceId) => (mark = id);
    const unMarkOsm = () => (mark = null);
    const getOsmMark = (id: OsmResourceId) =>
        mark !== null &&
        id.id === mark.id &&
        id.type === mark.type &&
        id.version === mark.version;

    return { markOsm, unMarkOsm, getOsmMark };
})();

const makeFeatures = (
    gs: DirectGeometryObject[],
    properties: Properties
): Feature[] =>
    gs.map(geometry => ({
        type: 'Feature',
        id: uniqId(),
        properties,
        geometry: wgs84toLB72(geometry),
    }));

const makeFetchData =
    (gs: DirectGeometryObject[], properties: Properties): FetchData =>
    () =>
        right(
            some({
                type: 'FeatureCollection',
                features: makeFeatures(gs, properties),
            })
        );

export const addOsmLayer = () =>
    getLastSelectedIssue()
        .chain(getOsmResourceId)
        .map(id => {
            if (getOsmMark(id)) {
                return;
            }
            unMarkOsm();
            removeLayer(QAMAPNAME, OSM_POINT);
            removeLayer(QAMAPNAME, OSM_LINE);
            removeLayer(QAMAPNAME, OSM_POLY);
            getOsmRemoteGeometries(id).foldL(
                () => {
                    window.setTimeout(addOsmLayer, 250);
                },
                ({ points, lines, polygons }) => {
                    markOsm(id);
                    const props: Properties = {
                        version: 'head',
                    };
                    addLayer(
                        QAMAPNAME,
                        () => getOsmSynthInfo(OSM_POINT, 'Point'),
                        makeFetchData(points, props)
                    );
                    addLayer(
                        QAMAPNAME,
                        () => getOsmSynthInfo(OSM_LINE, 'LineString'),
                        makeFetchData(lines, props)
                    );
                    addLayer(
                        QAMAPNAME,
                        () => getOsmSynthInfo(OSM_POLY, 'Polygon'),
                        makeFetchData(polygons, props)
                    );
                }
            );
        });

export const setMapBaseLayer = (id: string) =>
    assign('data/baselayer/select', id);

logger('loaded');
